import { useState } from "react";
import { ChevronRight } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import {
  Collapsible,
  CollapsibleTrigger,
  CollapsibleContent,
} from "@/components/ui/collapsible";
import { ClassificationBadge } from "@/components/locations/ClassificationBadge";
import { ConfidenceBadge } from "@/components/locations/ConfidenceBadge";
import { OwnershipBadge } from "@/components/locations/OwnershipBadge";
import type { OwnershipType } from "@/types/domain";

export interface ClassificationRow {
  id: string;
  source: "satellite" | "website" | "web_research";
  has_sport_field: boolean | null;
  user_has_sport_field: boolean | null;
  confidence: number | null;
  notes: string | null;
  model_used: string | null;
}

interface ClassificationsSectionProps {
  classifications: ClassificationRow[];
  ownership?: OwnershipType;
  hasWebsite?: boolean;
  isRejected?: boolean;
}

const ALL_SOURCES = ["satellite", "website", "web_research"] as const;

const SOURCE_LABELS: Record<string, string> = {
  satellite: "Satellite",
  website: "Website",
  web_research: "Web research",
};

function getPlaceholderText(
  source: string,
  hasWebsite: boolean,
  isRejected: boolean,
): string {
  switch (source) {
    case "satellite":
      return "Satellite classification has not been run yet.";
    case "website":
      return hasWebsite
        ? "Website classification has not been run yet."
        : "Location doesn't have a website.";
    case "web_research":
      return isRejected
        ? "Location was rejected before web research could run."
        : "Web research has not been run for this location yet.";
    default:
      return "Classification not available.";
  }
}

function ClassificationItem({
  classification,
  ownership,
}: {
  classification: ClassificationRow;
  ownership?: OwnershipType;
}) {
  const [open, setOpen] = useState(false);
  const sourceLabel = SOURCE_LABELS[classification.source] ?? classification.source;
  const confidence =
    classification.confidence != null
      ? Math.round(classification.confidence * 100)
      : null;

  return (
    <Collapsible open={open} onOpenChange={setOpen}>
      <CollapsibleTrigger className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-sm hover:bg-muted/50 transition-colors">
        <ChevronRight
          className={`size-4 shrink-0 text-muted-foreground transition-transform ${open ? "rotate-90" : ""}`}
        />
        <Badge variant="outline" className="text-xs shrink-0">
          {sourceLabel}
        </Badge>
        {classification.source === "website" ? (
          ownership && <OwnershipBadge ownership={ownership} className="text-xs" />
        ) : (
          <ClassificationBadge
            hasSportField={
              classification.user_has_sport_field ??
              classification.has_sport_field
            }
            className="text-xs"
          />
        )}
        {confidence != null && (
          <ConfidenceBadge confidence={confidence} className="text-xs ml-auto" />
        )}
      </CollapsibleTrigger>

      <CollapsibleContent className="pl-8 pr-2 pb-2 pt-1 space-y-3">
        {classification.notes && (
          <p className="text-xs text-muted-foreground italic">
            {classification.notes}
          </p>
        )}

        {classification.model_used && (
          <div className="text-xs text-muted-foreground">
            Model: {classification.model_used}
          </div>
        )}
      </CollapsibleContent>
    </Collapsible>
  );
}

function PlaceholderItem({
  source,
  hasWebsite,
  isRejected,
}: {
  source: string;
  hasWebsite: boolean;
  isRejected: boolean;
}) {
  const label = SOURCE_LABELS[source] ?? source;
  const text = getPlaceholderText(source, hasWebsite, isRejected);

  return (
    <div className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-sm">
      <div className="size-4 shrink-0" />
      <Badge variant="outline" className="text-xs shrink-0 opacity-50">
        {label}
      </Badge>
      <span className="text-xs text-muted-foreground italic">{text}</span>
    </div>
  );
}

export function ClassificationsSection({
  classifications,
  ownership,
  hasWebsite = false,
  isRejected = false,
}: ClassificationsSectionProps) {
  const classificationMap = new Map(
    classifications.map((c) => [c.source, c]),
  );

  return (
    <div className="space-y-1">
      {ALL_SOURCES.map((source) => {
        const classification = classificationMap.get(source);
        if (classification) {
          return (
            <ClassificationItem
              key={classification.id}
              classification={classification}
              ownership={ownership}
            />
          );
        }
        return (
          <PlaceholderItem
            key={source}
            source={source}
            hasWebsite={hasWebsite}
            isRejected={isRejected}
          />
        );
      })}
    </div>
  );
}
